import React from "react";
import { useHistory } from "react-router-dom";
import { Alert } from "@material-ui/lab";
import { makeStyles, Typography } from "@material-ui/core";
import { toast } from "react-toastify";

import iconLight from "../../assets/miscPng/icon.png";
import iconDark from "../../assets/miscPng/icon-dark.png";
import { i18n } from "../../translate/i18n";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";

const useStyles = makeStyles((theme) => ({
  //  ********************
  //  ** Main Container **
  //  ********************
  mainContainer: {
    display: "flex"
    , flexDirection: "column"
    , alignItems: "center"
    , justifyContent: "flex-start"
    , gap: "1em"
    , height: "100.00%"
    , padding: "1em"
    , color: theme.palette.text.primary
    , backgroundColor: theme.palette.background.paper
  }

  //  *******************
  //  ** Nav Container **
  //  *******************
  , navContainer: {
    width: "85.00%"
    , display: "flex"
    , flexDirection: "row"
    , alignItems: "center"
    , justifyContent: "space-between"
    , padding: "1em"
  }

  , navSubContainer: {
    display: "flex"
    , flexDirection: "row"
    , justifyContent: "center"
    , alignItems: "center"
  }

  , navAvatar: {
    borderRadius:"20px",
    margin: theme.spacing(1, 1, 1, 2),
    width: "35px",
    height: "auto",
    userSelect: "none",
  }

  , navTitle: {
    fontWeight: "bold"
    , fontSize: "2em"
  }

  , navBackButton: {
    fontSize: "1.30em"
    , color: theme.palette.secondary.ackCheck
    , cursor: "pointer"
    , transition: "all 0.30s ease"
    , "&:hover": {
      transform: "translateY(-5px)"
      , textDecoration: "underline"
      , textUnderlineOffset: "5px"
    }
  }

  //  **********************
  //  ** Header Container **
  //  **********************
  , headerContainer: {
    width: "85.00%"
    , display: "flex"
    , flexDirection: "column"
    , alignItems: "center"
    , justifyContent: "flex-start"
    , gap: "0.50em"
    , textIndent: "1em"
    , textAlign: "justify"
  }

  , headerTitle: {
    fontSize: "1.75em"
    , fontWeight: "bold"
  }

  , headerSubtitle: {
    fontSize: "1.25em"
  }

  //  ***********************
  //  ** Content Container **
  //  ***********************
  , contentContainer: {
    display: "flex"
    , flexDirection: "column"
    , alignItems: "center"
    , justifyContent: "center"
    , gap: "1em"
    , width: "85.00%"
    , fontSize: "1.25em"
    , textAlign: "justify"
    , textIndent: "1em"
  }

  , contentHr: {
    width: "100.00%"
  }

  , contentSubContainer: {
    width: "80.00%"
  }
  
  , contentTitle: {
    fontSize: "1.50em"
  }

  , contentList: {
    textIndent: "1px"
    , "& li": { paddingBottom: "3px" }
    , "& li span": { fontWeight: "bold" }
  }

  , contentAnchor: {
    cursor: "pointer"
    , color: theme.palette.secondary.ackCheck
    , transition: "all 0.30s ease"
    , "&:hover": {
      textDecoration: "underline"
    }
  }

  , contentBoldText: {
    textWeight: "bold",
  }

  //  **********************
  //  ** Footer Container **
  //  **********************
  , footerContainer: {
    width: "100.00%"
    , textAlign: "center"
    , fontSize: "1.00em"
  }
}));

const PrivacyPolicy = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();
  const currentIcon = localStorage.getItem("theme") ? iconDark : iconLight;


  //  ***************
  //  ** Functions **
  //  ***************
  const handleBack = () => {
    DefaultFunctionsUtils.redirectToTicketsPage(history);
  };

  const handleWhatsAppSupport = () => {
    const message = "Olá! Estou no site de Política e Privacidade e gostaria de atendimento!";
    window.open(`https://api.whatsapp.com/send?phone=5518991129967&text=${message.replaceAll(" ", "%20")}`);
  };

  const handleCopyInfo = async (event) => {
    await navigator.clipboard.writeText(event.target.innerText);
    toast.success(i18n.t("privacyPolicy.tooltips.contentCopied"));
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={classes.mainContainer}>
      {/* 
        *******************
        ** Nav Container **
        *******************
      */}
      <nav className={classes.navContainer}>
        <div className={classes.navSubContainer}>
          <span className={classes.navBackButton} onClick={handleBack}>
            ⬅️ Voltar
          </span>

          <img className={classes.navAvatar} src={currentIcon} alt="BestZap Logo" />

          <span className={classes.navTitle}>
            BestZap
          </span>
        </div>
      </nav>

      {/* 
        **********************
        ** Header Container **
        **********************
      */}
      <header className={classes.headerContainer}>
        <Typography className={classes.headerTitle}>
          POLÍTICA DE PRIVACIDADE
        </Typography>

        <Typography className={classes.headerSubtitle}>
          Última atualização: 01 de Junho de 2026. Saiba como a aplicação BestZap coleta, utiliza, processa e protege as suas informações e os dados obtidos de integrações oficiais.
        </Typography>
      </header>
      
      {/* 
        ***********************
        ** Content Container **
        ***********************
      */}
      
      <br />

      <section className={classes.contentContainer}>
        <div className={classes.contentSubContainer}>
          <Alert severity="info">
            <b>Compromisso Principal</b>
            <p>No BestZap, a privacidade e o sigilo das informações dos usuários são prioridades absolutas. Operamos em estrita conformidade com a Lei Geral de Proteção de Dados (LGPD) do Brasil e com as diretrizes e termos exigidos pelas plataformas associadas, como a Meta (WhatsApp Business Cloud API).</p>
          </Alert>
        </div>

        <div className={classes.contentSubContainer}>
          <h2 className={classes.contentTitle}>
            1. Informações que Coletamos
          </h2>

          <span>
            Coletamos dados necessários para a entrega dos nossos serviços de comunicação pelo WhatsApp. Esses dados englobam:
          </span>

          <ul className={classes.contentList}>
            <li>
              <span>Dados Cadastrais do Usuário:</span> nome completo, endereço de e-mail institucional, senha de acesso criptografada e credenciais de contato;
            </li>
            
            <li>
              <span>Dados de Integração do WhatsApp:</span> quando você integra o seu número na API Oficial da Meta (Embedded Signup), processamos e armazenamos as chaves de conexão autorizadas (tokens de acesso da Graph API, ID da Conta Comercial do WhatsApp, IDs de telefone), históricos de envio e recepção de mensagens para fins de exibição nos logs internos e templates aprovados pela Meta.
            </li>
          </ul>
        </div>

        <hr className={classes.contentHr} />

        <div className={classes.contentSubContainer}>
          <h2 className={classes.contentTitle}>
            2. Como Utilizamos os seus Dados
          </h2>

          <span>
            Todos os dados coletados são tratados única e exclusivamente para a prestação e melhoria contínua das automações da plataforma:
          </span>

          <ul className={classes.contentList}>
            <li>
              <span>Conversas:</span> permitir o recebimento e o envio de mensagens no WhatsApp entre os usuários e os contatos da conta conectada;
            </li>

            <li>
              <span>Estatísticas de Atendimentos:</span> permitir a análise e a geração de relatórios de acordo com as métricas de atendimento dos usuários;
            </li>
            
            <li>
              <span>Logs de Operação:</span> permitir o monitoramento auditável e controle de acessos executados por usuário da aplicação.
            </li>
          </ul>
        </div>

        <hr className={classes.contentHr} />

        <div className={classes.contentSubContainer}>
          <h2 className={classes.contentTitle}>
            3. Termos da Integração do WhatsApp (Meta Cloud API)
          </h2>

          <span>
            Ao utilizar a integração do WhatsApp do BestZap, você declara estar ciente e consentir com os seguintes termos específicos:
          </span>

          <ul className={classes.contentList}>
            <li>
              <span>Processador de Tecnologia:</span> a aplicação atua meramente como um integrador e processador tecnológico de mensagens através dos endpoints oficiais da Meta;
            </li>

            <li>
              <span>Privacidade do Conteúdo:</span> não monitoramos, interceptamos ou compartilhamos o conteúdo das conversas ou envios com terceiros. Suas mensagens de texto e disparos de templates são encriptados e trafegam direto para a infraestrutura oficial de servidores da Meta;
            </li>
            
            <li>
              <span>Controle de Credenciais:</span> você possui total autonomia e controle sobre as chaves concedidas. A qualquer momento, o administrador pode revogar a permissão do aplicativo e excluir definitivamente todas as credenciais de WhatsApp do banco de dados na aba de Conexões;
            </li>

            <li>
              <span>Logs de Visualização:</span> exibimos log de status (pendente, enviado, entregue, visualizado, falho) na conversa para mostrar ciência da etapa do envio de mensagens.
            </li>
          </ul>
        </div>

        <hr className={classes.contentHr} />

        <div className={classes.contentSubContainer}>
          <h2 className={classes.contentTitle}>
            4. Compartilhamento de Informações
          </h2>

          <span>
            O BestZap não vende, aluga ou compartilha suas informações pessoais com terceiros, sob nenhuma hipótese. O compartilhamento ocorre apenas quando indispensável para a execução técnica das integrações que você ativar:
          </span>

          <ul className={classes.contentList}>
            <li>
              <span>Facebook / Meta Graph API:</span> para entrega e recebimento de mensagens durante a conversa na aplicação;
            </li>

            <li>
              <span>Cumprimento Legal:</span> caso seja estritamente necessário para atender a determinação de ordem judicial.
            </li>
          </ul>
        </div>

        <hr className={classes.contentHr} />

        <div className={classes.contentSubContainer}>
          <h2 className={classes.contentTitle}>
            5. Segurança e Retenção de Dados
          </h2>

          <span>
            Para assegurar a máxima integridade e inviolabilidade dos dados e tokens de integração:
          </span>

          <ul className={classes.contentList}>
            <li>
              <span>Multi-Tenancy Isolado:</span> os bancos de dados de nossos clientes são logicamente isolados (um banco de dados independente por cliente). Isso elimina qualquer risco de vazamento cruzado de chaves e conversas;
            </li>

            <li>
              <span>Criptografia em Trânsito e Repouso:</span> todas as requisições utilizam canal TLS seguro (HTTPS). Senhas e chaves de conexões sensíveis no banco de dados são criptografadas com algoritmos robustos de Hash e Salt;
            </li>

            <li>
              <span>Exclusão Permanente:</span> a exclusão de um cliente (tenant) remove imediatamente todos os dados das empresas vinculadas, arquivos armazenados e configurações do WhatsApp.
            </li>
          </ul>
        </div>

        <hr className={classes.contentHr} />

        <div className={classes.contentSubContainer}>
          <h2 className={classes.contentTitle}>
            6. Seus Direitos (LGPD)
          </h2>

          <span>
            Você, como titular de dados, possui plenos direitos previstos em lei:
          </span>

          <ul className={classes.contentList}>
            <li>
              <span>Consultas:</span> acessar, confirmar a existência de tratamento e solicitar a correção de dados incompletos ou inexatos;
            </li>

            <li>
              <span>Revogações:</span> solicitar a revogação de consentimentos concedidos a qualquer momento;
            </li>

            <li>
              <span>Exclusões:</span> exigir a portabilidade ou a eliminação definitiva de dados pessoais de nossos registros, ressalvadas as obrigações fiscais de armazenamento legal.
            </li>
          </ul>
        </div>

        <hr className={classes.contentHr} />

        <div className={classes.contentSubContainer}>
          <h2 className={classes.contentTitle}>
            7. Instruções para Exclusão de Dados (Meta / WhatsApp)
          </h2>

          <span>
            Em conformidade com as diretrizes de privacidade da Meta e a LGPD, garantimos a possibilidade de solicitar a exclusão de seus respectivos dados e integrações a qualquer momento. Para solicitar a exclusão definitiva dos seus dados de nossa aplicação, siga o procedimento abaixo:
          </span>

          <ul className={classes.contentList}>
            <li>
              <span>Passo 1:</span> envia um e-mail para <span className={classes.contentAnchor} onClick={(event) => handleCopyInfo(event)}>fxsistemas@fxsistemas.com.br</span> com o assunto <span className={classes.contentBoldText}>"Exclusão de Dados"</span>;
            </li>

            <li>
              <span>Passo 2:</span> no corpo do e-mail, informe o nome completo ou o nome da empresa, o e-mail de cadastro na aplicação e o número do WhatsApp integrado (se aplicável).
            </li>
          </ul>
        </div>

        <hr className={classes.contentHr} />

        <div className={classes.contentSubContainer}>
          <h2 className={classes.contentTitle}>
            8. Alterações nesta Política
          </h2>

          <span>
            Podemos atualizar esta política de privacidade periodicamente para refletir mudanças regulatórias, novas funcionalidades de nossa aplicação ou alteração nas diretrizes de parceiros integrados. Quaisquer atualizações relevantes serão notificadas de forma transparente no painel administrativo principal.
          </span>
        </div>

        <hr className={classes.contentHr} />

        <div className={classes.contentSubContainer}>
          <h2 className={classes.contentTitle}>
            9. Fale Conosco
          </h2>

          <span>
            Caso tenha qualquer dúvida, necessite de esclarecimentos adicionais sobre a nossa Política de Privacidade ou precise solicitar a revogação de consentimentos de integrações do WhatsApp, entre em contato direto com a nossa equipe de suporte:
          </span>

          <ul className={classes.contentList}>
            <li>
              <span>E-Mail DPO / Suporte:</span> <span className={classes.contentAnchor} onClick={(event) => handleCopyInfo(event)}>fxsistemas@fxsistemas.com.br</span>;
            </li>

            <li>
              <span>Suporte WhatsApp:</span> <span className={classes.contentAnchor} onClick={handleWhatsAppSupport}>+55 (18) 99112-9967</span>.
            </li>
          </ul>
        </div>

        <hr className={classes.contentHr} />
      </section>

      {/* 
        ********************
        ** Content Footer **
        ********************
      */}
      <footer className={classes.footerContainer}>
        <section>
          © 2026 FX Sistemas / BestZap. Todos os direitos reservados. CNPJ: 07.413.946/0001-29
        </section>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
